import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

// rsuite
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'

// includes
import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import {isEmpty} from 'lodash'
import {toast} from 'react-toastify'
import {SortType} from 'rsuite-table/lib/@types/common'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {ItemType, StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as nabilAuctionRedux from '../index'
import AddNabilAuction from './AddNabilAuction'

const NabilAuctionManagerComponent = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [actionType, setActionType] = useState('Add')
  const {
    data,
    loading,
    singleEnableSuccess,
    singleDisableSuccess,
    enableSuccess,
    disableSuccess,
    toggleLoading,
    success,
    deleteSuccess,
    sortNabilAuctionData,
  } = useSelector((state: any) => state.nabilAuction)
  const [params, setParams] = useState<StateParamsModel>({
    page: 1,
    limit: 10,
  })
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])
  const [editSelectedData, setEditCheckedData] = useState<any>()

  // alert modal
  const [alertOpen, setAlertOpen] = useState(false)
  const handleAlertOpen = () => setAlertOpen(true)
  const handleAlertClose = () => setAlertOpen(false)
  const [openSortModal, setOpenSortModal] = useState(false)

  const handleChangeLimit = (dataKey: number) => {
    setParams({
      ...params,
      limit: dataKey,
    })
  }

  const enableNabilAuctionData = () => {
    if (!isEmpty(checkedValues)) {
      const formData = [...checkedValues]
      dispatch(nabilAuctionRedux.actions.enableNabilAuction(formData))
    } else {
      toast.error('No data Selected')
    }
  }

  const disableNabilAuctionData = () => {
    if (!isEmpty(checkedValues)) {
      const formData = [...checkedValues]
      dispatch(nabilAuctionRedux.actions.disableNabilAuction(formData))
    } else {
      toast.error('No data Selected')
    }
  }

  const ActionCell = ({rowData, dataKey, ...props}: any) => {
    return (
      <Cell {...props} className='link-group' data-cy='nabil-auction-link-group'>
        <CheckPermissions type='Edit'>
          <Whisper
            placement='top'
            trigger='hover'
            speaker={<Tooltip>Edit</Tooltip>}
          >
            <IconButton
              appearance='subtle'
              onClick={() => {
                setEditCheckedData(rowData)
                setActionType('Edit')
                setOpen(true)
              }}
              icon={<Edit2 />}
              data-cy='edit-button'
            />
          </Whisper>
        </CheckPermissions>
        <CheckPermissions type='Delete'>
          <Whisper
            placement='top'
            trigger='hover'
            speaker={<Tooltip>Delete</Tooltip>}
          >
            <IconButton
              appearance='subtle'
              onClick={() => {
                handleAlertOpen()
                let nabilAuctionId = [rowData.id]
                setCheckedValues(nabilAuctionId)
              }}
              icon={<TrashIcon />}
              data-cy='delete-button'
            />
          </Whisper>
        </CheckPermissions>
        <Whisper
          placement='top'
          trigger='hover'
          speaker={<Tooltip>Status</Tooltip>}
        >
          <Toggle
            size='sm'
            disabled={toggleLoading}
            checked={rowData.status}
            onClick={() => handleToggleAction(rowData)}
            data-cy='toggle-button'
          />
        </Whisper>
      </Cell>
    )
  }

  const handleToggleAction = (data: {[key: string]: string}) => {
    data?.status
      ? dispatch(nabilAuctionRedux.actions.singleDisableNabilAuction(data.id))
      : dispatch(nabilAuctionRedux.actions.singleEnableNabilAuction(data.id))
  }

  const Cell = Table.Cell

  const handleClose = () => setOpen(false)

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 60,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Name',
      dataKey: 'name',
      flexGrow: 1,
      cell: <Cell dataKey='name' />,
      sortable: true,
    },
    {
      label: 'Category',
      dataKey: 'category_name',
      flexGrow: 1,
      cell: <Cell dataKey='category_name' />,
      sortable: false,
    },
    {
      label: 'Publish Date',
      dataKey: 'publish_date',
      flexGrow: 1,
      cell: <Cell dataKey='publish_date' />,
      sortable: true,
    },
    {
      label: 'Expiry Date',
      dataKey: 'expiry_date',
      flexGrow: 1,
      cell: <Cell dataKey='expiry_date' />,
      sortable: true,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 85,
      cell: (
        <Cell>
          {(rowData) =>
            rowData?.status ? (
              <Tag color='green' size='sm'>
                Active
              </Tag>
            ) : (
              <Tag color='red' size='sm'>
                Inactive
              </Tag>
            )
          }
        </Cell>
      ),
      sortable: false,
    },
    {
      label: 'Action',
      width: 150,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
    },
  ]

  useEffect(() => {
    dispatch(nabilAuctionRedux.actions.getNabilAuction(params))
  }, [params])

  useEffect(() => {
    dispatch(nabilAuctionRedux.actions.getNabilAuctionCategoryList())
  }, [])

  const handleChecked = (values: Array<string>) => {
    setCheckedValues(values)
  }

  useEffect(() => {
    if (deleteSuccess) {
      toast.success('Nabil Auction deleted successfully')
    }
    if (enableSuccess) {
      toast.success('Nabil Auction enabled successfully')
    }
    if (disableSuccess) {
      toast.success('Nabil Auction disabled successfully')
    }
    handleChecked([])
    dispatch(nabilAuctionRedux?.actions.getNabilAuction(params))
  }, [deleteSuccess, success, enableSuccess, disableSuccess])

  useEffect(() => {
    if (singleEnableSuccess) {
      toast.success('Nabil Auction enabled successfully')
    }
    if (singleDisableSuccess) {
      toast.success('Nabil Auction disabled successfully')
    }
    handleChecked([])
  }, [singleDisableSuccess, singleEnableSuccess])

  const nabilAuctionData = data?.nabilAuction
    ? data?.nabilAuction?.map((item: any, i: number) => ({
        ...item,
        sn: (params?.page - 1) * params?.limit + (i + 1),
      }))
    : []

  const handleSort = (dataKey: string, sortType?: SortType) => {
    setParams({...params, orderBy: dataKey, order: sortType})
  }

  const handleAddModal = () => {
    setEditCheckedData({})
    setOpen(true)
    setActionType('Add')
  }

  const handleDelete = () => {
    dispatch(nabilAuctionRedux?.actions?.deleteNabilAuction([...checkedValues]))
    handleAlertClose()
  }

  const handleRefresh = () => {
    dispatch(nabilAuctionRedux.actions.getNabilAuction(params))
  }

  const handleGetAllData = () => {
    setParams({...params, limit: data?.meta?.total})
    dispatch(nabilAuctionRedux.actions.getNabilAuction(params))
  }

  const handleSubmitSort = (data: Array<ItemType>) => {
    let body = {
      nabilAuctionId: data?.map((item) => ({id: item?.keyId})),
    }
    dispatch(nabilAuctionRedux.actions.sortNabilAuction(body))
  }

  useEffect(() => {
    if (!isEmpty(sortNabilAuctionData) && success) {
      toast.success('Nabil Auction sorted successfully')
      dispatch(nabilAuctionRedux.actions.sortNabilAuctionReset())
      setParams({...params, limit: 10})
      dispatch(nabilAuctionRedux.actions.getNabilAuction(params))
      setOpenSortModal(false)
    }
  }, [sortNabilAuctionData, success])

  return (
    <>
      <div className='shadow p-3 bg-white rounded' data-cy='nabil-auction-shadow-comp'>
        <DesignComponent
          moduleName='Nabil Auction Manager'
          params={params}
          setParams={setParams}
          handleRefresh={handleRefresh}
          handleAddModal={handleAddModal}
          handleAlertOpen={handleAlertOpen}
          exportShow={false}
          importShow={false}
          enableMultiple={enableNabilAuctionData}
          disableMultiple={disableNabilAuctionData}
          data={nabilAuctionData}
          sortShow={false}
          sortButtonName='Sort'
          handleSubmitSort={handleSubmitSort}
          handleGetAllData={handleGetAllData}
          openSortModal={openSortModal}
          setOpenSortModal={setOpenSortModal}
          loading={loading}
          data-cy='nabil-auction-design-comp'
        />

        <div className='datatable' data-cy='nabil-auction-datatable'>
          <RSuiteTable
            onChecked={handleChecked}
            showCheckbox={true}
            columns={columns}
            data={nabilAuctionData}
            checkedValues={checkedValues}
            showLoading={loading}
            handleSort={handleSort}
            data-cy='nabil-auction-r-suite'
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={data?.meta?.total || 0}
            limitOptions={[10, 20, 50, 100]}
            limit={params?.limit}
            activePage={params?.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={handleChangeLimit}
            data-cy='nabil-auction-pagination'
          />
          {open && (
            <AddNabilAuction
              open={open}
              params={params}
              handleClose={handleClose}
              actionType={actionType}
              editSelectedData={editSelectedData}
            />
          )}
        </div>

        {/* Delete Modal */}
        <>
          {alertOpen &&
            (!isEmpty(checkedValues) ? (
              <DeleteModal
                handleClick={() => handleDelete()}
                isOpen={alertOpen}
                handleClose={() => handleAlertClose()}
              />
            ) : (
              toast.error('No data selected') && setAlertOpen(false)
            ))}
        </>
      </div>
    </>
  )
}

export default NabilAuctionManagerComponent
